import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingCart, Check, Minus, Plus, MessageCircle, FileText, BookOpen, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { resolveImg } from "../lib/api";
import { toast } from "sonner";
import { formatJOD } from "../components/ProductCard";
import Stars from "../components/Stars";
import ProductHighlights from "../components/ProductHighlights";
import BtuCalculator from "../components/BtuCalculator";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState("");
  const [rating, setRating] = useState({ avg: 0, count: 0 });
  const [variantId, setVariantId] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const { addItem } = useCart();
  const { lang, t, tBrand, tType } = useLang();

  const displayName = lang === "ar" && product?.name_ar ? product.name_ar : product?.name;
  const displayDescription = lang === "ar" && product?.description_ar ? product.description_ar : product?.description;
  const galleryImages = (product?.images || []).filter(Boolean);

  // Reset gallery selection when product slug changes (navigating between products)
  useEffect(() => {
    setActiveImg(0);
  }, [slug]);

  useEffect(() => {
    setError("");
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        const variants = r.data?.variants || [];
        if (variants.length) {
          // Default to the smallest-BTU (entry-level) variant
          const entry = [...variants].sort(
            (a, b) => Number(a.btu || 0) - Number(b.btu || 0)
          )[0];
          setVariantId(entry.id);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || t("product.notFound")));
    api.get("/ratings").then((r) => {
      const stats = r.data?.[slug];
      if (stats) setRating(stats);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const variant = useMemo(() => {
    if (!product?.variants?.length) return null;
    return product.variants.find((v) => v.id === variantId) || product.variants[0];
  }, [product, variantId]);

  if (error)
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 text-center">
        <h1 className="font-display font-black text-3xl">{t("product.notFound")}</h1>
        <Link to="/shop" className="mt-4 inline-block text-electric underline">{t("product.backToShop")}</Link>
      </div>
    );
  if (!product) return <div className="py-24 text-center text-slate-500 font-mono">{t("common.loading")}</div>;

  const price = variant
    ? (variant.sale_price || variant.price)
    : (product.sale_price || product.price);
  const fullPrice = variant ? variant.price : product.price;
  const onSale = variant ? variant.sale_price : product.sale_price;
  const stock = variant ? variant.stock : product.stock;
  const outOfStock = stock <= 0;
  const quoteOnly = !!product.quote_only;

  const waMessage = quoteOnly
    ? (lang === "ar"
        ? `مرحباً شمس الشارقة، أرغب بعرض سعر لمنتج: ${displayName}${variant ? ` - ${variant.label}` : ""} × ${qty}`
        : `Hi Shams Al Shareqa, I'd like a quote for: ${displayName}${variant ? ` - ${variant.label}` : ""} × ${qty}`)
    : lang === "ar"
    ? variant
      ? `مرحباً شمس تريد، أرغب بطلب: ${displayName} - ${variant.label} بسعر ${price.toFixed(2)} د.أ × ${qty}`
      : `مرحباً شمس تريد، أرغب بطلب: ${displayName} بسعر ${price.toFixed(2)} د.أ × ${qty}`
    : variant
    ? `Hi Shamstrade, I'd like to order: ${displayName} - ${variant.label} at ${price.toFixed(2)} JOD × ${qty}`
    : `Hi Shamstrade, I'd like to order: ${displayName} at ${price.toFixed(2)} JOD × ${qty}`;

  const reviewWord = (n) => (n === 1 ? t("product.review") : t("product.reviews"));

  return (
    <div data-testid="product-detail-page" className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <Link to="/shop" className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-electric">
        {t("product.backToCatalog")}
      </Link>
      <div className="mt-6 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <button
            type="button"
            onClick={() => galleryImages.length > 0 && setLightbox(true)}
            data-testid="gallery-main"
            className="block w-full bg-white rounded-2xl aspect-square overflow-hidden shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] group"
          >
            {galleryImages[activeImg] && (
              <img
                src={resolveImg(galleryImages[activeImg])}
                alt={displayName}
                className="w-full h-full object-contain p-10 transition-transform duration-500 group-hover:scale-105"
              />
            )}
          </button>
          {galleryImages.length > 1 && (
            <div
              data-testid="gallery-thumbnails"
              className={`mt-3 grid gap-2`}
              style={{ gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 6)}, minmax(0, 1fr))` }}
            >
              {galleryImages.slice(0, 8).map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`aspect-square rounded-lg overflow-hidden bg-white border-2 transition-all ${
                    i === activeImg ? "border-ink ring-2 ring-electric/30" : "border-transparent hover:border-black/20"
                  }`}
                >
                  <img
                    src={resolveImg(src)}
                    alt=""
                    className="w-full h-full object-contain p-1"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">{tBrand(product.brand)}</div>
          <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tighter leading-none">
            {displayName}
          </h1>
          {rating.count > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <Stars value={rating.avg} size={16} />
              <span className="text-sm font-mono text-slate-500">{rating.avg.toFixed(1)} · {rating.count} {reviewWord(rating.count)}</span>
            </div>
          )}
          <div className="mt-4 flex items-baseline gap-3">
            {quoteOnly ? (
              <span
                data-testid="detail-quote-only"
                className="inline-flex items-center gap-2 px-3 py-2 bg-electric/10 text-electric text-xs font-mono uppercase tracking-widest"
              >
                <MessageCircle size={14} /> {t("common.priceOnRequest")}
              </span>
            ) : (
              <>
                <span data-testid="detail-price" className="font-display font-black text-3xl">{formatJOD(price)}</span>
                {onSale && (
                  <span className="font-mono text-slate-400 line-through text-sm">
                    {formatJOD(fullPrice)}
                  </span>
                )}
              </>
            )}
          </div>
          <p className="mt-6 text-slate-700 leading-relaxed">{displayDescription}</p>

          {/* Variant selector */}
          {product.variants && product.variants.length > 0 && (
            <div className="mt-8" data-testid="variant-selector">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">
                {t("product.chooseSize")}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {product.variants.map((v) => {
                  const isActive = v.id === variantId;
                  const vPrice = v.sale_price || v.price;
                  const isOut = (v.stock || 0) <= 0;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      disabled={isOut}
                      onClick={() => setVariantId(v.id)}
                      data-testid={`variant-${v.btu}`}
                      className={`text-left p-3 rounded-xl transition-all duration-300 ${
                        isActive
                          ? "bg-ink text-white shadow-[0_4px_20px_-4px_rgba(0,85,255,0.4)]"
                          : "bg-secondary hover:bg-ink hover:text-white"
                      } ${isOut ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <div className="font-display font-bold text-sm">{v.label}</div>
                      {!quoteOnly && (
                        <div className="mt-1 text-xs font-mono">
                          {formatJOD(vPrice)}
                          {v.sale_price && (
                            <span className="ml-1 line-through opacity-60">{formatJOD(v.price)}</span>
                          )}
                        </div>
                      )}
                      {isOut && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-rose-400">{t("common.outOfStock")}</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Inline BTU calculator — helps customer pick the right variant */}
          <div className="mt-6">
            <BtuCalculator
              compact
              showBreakdown={false}
              onResult={(r) => {
                // Auto-jump to the matching variant if one exists
                if (!product?.variants?.length) return;
                const closest = product.variants
                  .map((v) => {
                    const m = String(v.label || "").match(/(\d{3,6})/);
                    return m ? { v, diff: Math.abs(Number(m[1]) - r.recommended) } : null;
                  })
                  .filter(Boolean)
                  .sort((a, b) => a.diff - b.diff)[0];
                if (closest && closest.v.id !== variantId && (closest.v.stock || 0) > 0) {
                  setVariantId(closest.v.id);
                }
              }}
            />
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 text-sm font-mono">
            {(variant?.btu || product.specs?.btu) && (
              <div className="bg-secondary rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("product.specs.btu")}</div>
                <div className="font-semibold mt-1">{variant?.btu || product.specs.btu}</div>
              </div>
            )}
            {product.specs?.type && (
              <div className="bg-secondary rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("product.specs.type")}</div>
                <div className="font-semibold mt-1 uppercase">{tType(product.specs.type)}</div>
              </div>
            )}
            {product.specs?.energy_rating && (
              <div className="bg-secondary rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("product.specs.energy")}</div>
                <div className="font-semibold mt-1">{product.specs.energy_rating}</div>
              </div>
            )}
            {product.specs?.warranty && (
              <div className="bg-secondary rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("product.specs.warranty")}</div>
                <div className="font-semibold mt-1">{product.specs.warranty}</div>
              </div>
            )}
          </div>

          <div className="mt-8 flex items-stretch gap-3">
            {!quoteOnly && (
              <div className="flex items-center bg-secondary rounded-lg">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  data-testid="qty-decrease"
                  className="w-11 h-11 grid place-items-center rounded-lg hover:bg-ink hover:text-white"
                  aria-label="−"
                >
                  <Minus size={14} />
                </button>
                <div data-testid="qty-display" className="w-12 text-center font-mono font-semibold">{qty}</div>
                <button
                  onClick={() => setQty(qty + 1)}
                  data-testid="qty-increase"
                  className="w-11 h-11 grid place-items-center rounded-lg hover:bg-ink hover:text-white"
                  aria-label="+"
                >
                  <Plus size={14} />
                </button>
              </div>
            )}
            {!quoteOnly && (
              <button
                data-testid="detail-add-to-cart"
                disabled={outOfStock}
                onClick={() => {
                  addItem(product, qty, variant);
                  toast.success(`${displayName}${variant ? ` · ${variant.label}` : ""} ${t("common.addedToCart")}`);
                }}
                className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-ink text-white hover:bg-electric transition-colors text-xs font-mono uppercase tracking-[0.2em] disabled:opacity-50"
              >
                <ShoppingCart size={14} /> {outOfStock ? t("common.outOfStock") : t("common.addToCart")}
              </button>
            )}
          </div>

          <a
            data-testid="detail-whatsapp"
            href={`https://wa.me/962799448817?text=${encodeURIComponent(waMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`${quoteOnly ? "mt-6 h-11" : "mt-3 h-11"} w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] text-white hover:bg-[#1ebe5b] transition-colors text-xs font-mono uppercase tracking-[0.18em] px-3 whitespace-nowrap overflow-hidden`}
          >
            <MessageCircle size={14} className="shrink-0" />
            <span className="truncate">{quoteOnly ? t("common.requestQuote") : t("common.orderOnWhatsApp")}</span>
          </a>

          <div className="mt-6 flex flex-col gap-2 text-xs font-mono uppercase tracking-widest text-slate-500">
            <div className="flex items-center gap-2"><Check size={14} className="text-electric" /> {t("common.cashOnDelivery")}</div>
            <div className="flex items-center gap-2"><Check size={14} className="text-electric" /> {t("common.freeDelivery")}</div>
          </div>

          {(() => {
            const tp = String(product?.specs?.type || "").toLowerCase();
            if (tp !== "duct" && tp !== "cassette") return null;
            return (
              <div
                data-testid="free-design-banner"
                className="mt-5 rounded-xl bg-gradient-to-r from-electric/10 to-frost border border-electric/30 p-4 flex items-start gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-electric/15 text-electric grid place-items-center shrink-0">
                  <Sparkles size={18} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm">{t("common.freeDesign")}</div>
                  <div className="text-xs text-slate-600 mt-1 leading-relaxed">{t("common.freeDesignSub")}</div>
                </div>
              </div>
            );
          })()}

          {(product.spec_pdf_url || product.manual_pdf_url) && (
            <div className="mt-5 flex flex-wrap gap-2">
              {product.spec_pdf_url && (
                <a
                  href={resolveImg(product.spec_pdf_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-spec-pdf"
                  className="inline-flex items-center gap-2 h-10 px-4 border border-ink hover:bg-ink hover:text-white transition-colors text-[11px] font-mono uppercase tracking-widest"
                >
                  <FileText size={14} /> {t("common.downloadSpec")}
                </a>
              )}
              {product.manual_pdf_url && (
                <a
                  href={resolveImg(product.manual_pdf_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-manual-pdf"
                  className="inline-flex items-center gap-2 h-10 px-4 border border-ink hover:bg-ink hover:text-white transition-colors text-[11px] font-mono uppercase tracking-widest"
                >
                  <BookOpen size={14} /> {t("common.downloadManual")}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <ProductHighlights
        items={product.highlights || []}
        lang={lang}
        label={t("product.keyFeatures")}
      />

      {(product.long_description || []).length > 0 && (
        <section data-testid="detail-long-description" className="mt-16 border-t border-black/10 pt-12">          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">
            {t("product.aboutThisItem")}
          </div>
          <h2 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl tracking-tight">
            {lang === "ar" && product.name_ar ? product.name_ar : product.name}
          </h2>

          <div className="mt-10 space-y-16">
            {product.long_description.map((block, i) => {
              const heading = lang === "ar" ? (block.heading_ar || block.heading) : block.heading;
              const body = lang === "ar" ? (block.body_ar || block.body) : block.body;
              const reverse = i % 2 === 1;
              // Layout mode 1 — image already has typography baked in: render
              // edge-to-edge with no overlay or side text.
              if (block.image_url && block.image_has_text) {
                return (
                  <div
                    key={i}
                    data-testid={`detail-long-desc-${i}`}
                    className="rounded-2xl overflow-hidden bg-secondary"
                  >
                    <img
                      src={resolveImg(block.image_url)}
                      alt={heading || ""}
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                  </div>
                );
              }

              // Layout mode 2 — image + text overlay at admin-chosen (x,y).
              // Falls back to legacy side-by-side when no x/y override is set.
              const hasOverlay = block.image_url && (
                typeof block.text_x === "number" || typeof block.text_y === "number"
              );
              const tx = typeof block.text_x === "number" ? block.text_x : 50;
              const ty = typeof block.text_y === "number" ? block.text_y : 50;

              if (hasOverlay && (heading || body)) {
                // Anchor text horizontally so it stays within the image bounds
                const anchorX = tx < 33 ? "left" : tx > 66 ? "right" : "center";
                const transform =
                  anchorX === "left"
                    ? "translate(0, -50%)"
                    : anchorX === "right"
                    ? "translate(-100%, -50%)"
                    : "translate(-50%, -50%)";
                return (
                  <div
                    key={i}
                    data-testid={`detail-long-desc-${i}`}
                    className="relative rounded-2xl overflow-hidden bg-secondary aspect-[16/9] sm:aspect-[2/1]"
                  >
                    <img
                      src={resolveImg(block.image_url)}
                      alt={heading || ""}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div
                      className={`absolute max-w-md p-5 sm:p-6 bg-black/55 backdrop-blur-sm rounded-xl text-white text-${anchorX}`}
                      style={{ left: `${tx}%`, top: `${ty}%`, transform }}
                    >
                      {heading && (
                        <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight mb-2">
                          {heading}
                        </h3>
                      )}
                      {body && (
                        <p className="text-sm sm:text-base text-white/90 leading-relaxed whitespace-pre-line">
                          {body}
                        </p>
                      )}
                    </div>
                  </div>
                );
              }

              // Layout mode 3 — legacy side-by-side (kept for older blocks
              // without explicit positioning).
              return (
                <div
                  key={i}
                  data-testid={`detail-long-desc-${i}`}
                  className={`grid gap-8 lg:gap-12 items-center ${block.image_url ? "lg:grid-cols-2" : ""} ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
                >
                  {block.image_url && (
                    <div className="bg-secondary rounded-2xl overflow-hidden aspect-[4/3]">
                      <img
                        src={resolveImg(block.image_url)}
                        alt={heading || ""}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div>
                    {heading && (
                      <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight mb-3">
                        {heading}
                      </h3>
                    )}
                    {body && (
                      <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                        {body}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {lightbox && galleryImages[activeImg] && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setLightbox(false)}
          className="fixed inset-0 z-[100] bg-black/85 grid place-items-center p-4 sm:p-10 cursor-zoom-out"
        >
          <img
            src={resolveImg(galleryImages[activeImg])}
            alt={displayName}
            className="max-w-full max-h-full object-contain"
          />
          {galleryImages.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {galleryImages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setActiveImg(i); }}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    i === activeImg ? "bg-white scale-125" : "bg-white/40 hover:bg-white/70"
                  }`}
                  aria-label={`Image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
